import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * Generate filled delivery note HTML for preview
 * Returns the HTML string instead of opening in new tab
 */
export const generateDeliveryNotePreviewHTML = async (deliveryNote, storeInfo, t = (key) => key) => {
  try {
    if (!storeInfo) {
      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return null;
    }

    const templateHtml = await fetch('/deliverynotetemplates/deliverynote-template-en.html').then(r => r.text());

    const lineItems = deliveryNote.LineItems || [];
    const ProductRows = lineItems.map((p, i) => `
      <tr${i % 2 === 1 ? ' class="alternate-row"' : ''}>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${i + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${p.DisplayName || p.ProductName || p.Name || ''}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt; text-align: center;">${p.Quantity || 0}</td>
      </tr>
    `).join('');

    const rawDate = deliveryNote.IssueDate || deliveryNote.DeliveryNoteDate || deliveryNote.CreatedDate;
    const d = rawDate ? new Date(rawDate) : new Date();
    const formattedDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    const deliveryTime = deliveryNote.DeliveryTime || '';
    const formattedDateTime = deliveryTime ? `${formattedDate} ${deliveryTime}` : formattedDate;

    // Extract customer data - could be nested in Customer object or at root level
    const customer = deliveryNote.Customer || {};
    const customerName = customer.CompanyName || customer.Name || deliveryNote.CustomerName || '';
    const customerVAT = customer.VATNumber || customer.VAT || customer.VatNumber || deliveryNote.CustomerVAT || deliveryNote.CustomerVATNumber || '';
    const customerEmail = customer.Email || deliveryNote.CustomerEmail || '';
    const customerPhone = customer.PhoneNumber || customer.MobilePhone || deliveryNote.CustomerPhone || deliveryNote.CustomerPhoneNumber || '';
    const customerStreet = customer.Street || deliveryNote.CustomerAddress || deliveryNote.CustomerStreet || '';
    const customerCity = customer.City || deliveryNote.CustomerCity || '';
    const customerPostCode = customer.PostCode || deliveryNote.CustomerPostCode || '';
    const customerCountry = customer.Country || deliveryNote.CustomerCountry || '';
    const customerCityCountry = [customerPostCode, customerCity, customerCountry].filter(Boolean).join(' ');

    let filledTemplate = templateHtml
      .replace(/{{DocTitle}}/g, t('delivery_note') || 'DELIVERY NOTE')
      .replace(/{{DateLabel}}/g, t('date') || 'Date')
      .replace(/{{FromLabel}}/g, t('from') || 'From')
      .replace(/{{ToLabel}}/g, t('to') || 'To')
      .replace(/{{ProductHeader}}/g, t('product') || 'Product')
      .replace(/{{QuantityHeader}}/g, t('quantity') || 'Quantity')
      .replace(/{{TotalLabel}}/g, t('total') || 'Total')
      .replace(/{{FooterText}}/g, t('thank_you_for_your_business') || 'Thank you for your business!')
      .replace(/{{CompanyName}}/g, storeInfo?.name || '')
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || '')
      .replace(/{{CompanyEmail}}/g, storeInfo?.email || '')
      .replace(/{{CompanyPhone}}/g, storeInfo?.phone || '')
      .replace(/{{CompanyAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCity}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankAccount}}/g, storeInfo?.iban || '')
      .replace(/{{CustomerName}}/g, customerName)
      .replace(/{{CustomerId}}/g, String(deliveryNote.CustomerId || customer.Id || ''))
      .replace(/{{CustomerVAT}}/g, customerVAT)
      .replace(/{{CustomerNumber}}/g, String(deliveryNote.CustomerId || customer.Id || ''))
      .replace(/{{CustomerEmail}}/g, customerEmail)
      .replace(/{{CustomerPhone}}/g, customerPhone)
      .replace(/{{CustomerAddress}}/g, customerStreet)
      .replace(/{{CustomerCity}}/g, customerCity)
      .replace(/{{CustomerStreetAddress}}/g, customerStreet)
      .replace(/{{CustomerCityCountry}}/g, customerCityCountry)
      .replace(/{{DeliveryNoteNumber}}/g, deliveryNote.DeliveryNoteNumber || '')
      .replace(/{{DeliveryNoteDate}}/g, formattedDateTime)
      .replace(/{{DeliveryTime}}/g, deliveryTime)
      .replace(/{{Status}}/g, '')
      .replace(/status-{{Status}}/g, '')
      .replace(/{{LineItems}}/g, ProductRows)
      .replace(/{{{ProductRows}}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, String(deliveryNote.TotalQuantity || 0))
      .replace(/{{{DiscountRow}}}/g, '')
      .replace(/{{Subtotal}}/g, `€${parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2)}`)
      .replace(/{{TotalExcl}}/g, parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2))
      .replace(/{{VATPercentage}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{VAT}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{TotalVAT}}/g, parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2))
      .replace(/{{VATAmount}}/g, `€${parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2)}`)
      .replace(/{{Discount}}/g, '€0.00')
      .replace(/{{TotalAmount}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{Total}}/g, `€${parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2)}`)
      .replace(/{{GrandTotal}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{InvoicePaidAmount}}/g, parseFloat(deliveryNote.PaidAmount || deliveryNote.InvoicePaidAmount || 0).toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, parseFloat(deliveryNote.DueAmount || deliveryNote.InvoiceDueAmount || deliveryNote.TotalValue || deliveryNote.TotalAmount || 0).toFixed(2))
      .replace(/{{{PaymentMethodsSection}}}/g, '')
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1')
      .replace(/{{VATSection}}/g, '')
      .replace(/{{CustomText}}/g, deliveryNote.Notes || deliveryNote.CustomText || '')
      .replace(/{{CustomTextSection}}/g, (deliveryNote.Notes || deliveryNote.CustomText) ? `<div style="margin-top: 16px; padding: 12px; background: #f9f9f9; border-left: 3px solid #667eea; font-size: 8.5pt;"><strong>Notes:</strong><br>${deliveryNote.Notes || deliveryNote.CustomText}</div>` : '')
      .replace(/{{Notes}}/g, deliveryNote.Notes || '');

    return filledTemplate;
  } catch (error) {
    toast.error(t('failed_to_generate_preview') || 'Failed to generate preview. Please try again.');
    return null;
  }
};

/**
 * Generate and preview a delivery note PDF in a new browser tab.
 * Accepts the raw API delivery note object plus storeInfo from the Redux store.
 */
export const generateAndPreviewDeliveryNotePDF = async (deliveryNote, storeInfo, t = (key) => key) => {
  try {
    if (!storeInfo) {
      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return false;
    }

    const templateHtml = await fetch('/deliverynotetemplates/deliverynote-template-en.html').then(r => r.text());

    const lineItems = deliveryNote.LineItems || [];
    const ProductRows = lineItems.map((p, i) => `
      <tr${i % 2 === 1 ? ' class="alternate-row"' : ''}>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${i + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${p.DisplayName || p.ProductName || p.Name || ''}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt; text-align: center;">${p.Quantity || 0}</td>
      </tr>
    `).join('');

    const rawDate = deliveryNote.IssueDate || deliveryNote.DeliveryNoteDate || deliveryNote.CreatedDate;
    const d = rawDate ? new Date(rawDate) : new Date();
    const formattedDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    const deliveryTime = deliveryNote.DeliveryTime || '';
    const formattedDateTime = deliveryTime ? `${formattedDate} ${deliveryTime}` : formattedDate;

    // Extract customer data - could be nested in Customer object or at root level
    const customer = deliveryNote.Customer || {};
    const customerName = customer.CompanyName || customer.Name || deliveryNote.CustomerName || '';
    const customerVAT = customer.VATNumber || customer.VAT || customer.VatNumber || deliveryNote.CustomerVAT || deliveryNote.CustomerVATNumber || '';
    const customerEmail = customer.Email || deliveryNote.CustomerEmail || '';
    const customerPhone = customer.PhoneNumber || customer.MobilePhone || deliveryNote.CustomerPhone || deliveryNote.CustomerPhoneNumber || '';
    const customerStreet = customer.Street || deliveryNote.CustomerAddress || deliveryNote.CustomerStreet || '';
    const customerCity = customer.City || deliveryNote.CustomerCity || '';
    const customerPostCode = customer.PostCode || deliveryNote.CustomerPostCode || '';
    const customerCountry = customer.Country || deliveryNote.CustomerCountry || '';
    const customerCityCountry = [customerPostCode, customerCity, customerCountry].filter(Boolean).join(' ');

    let filledTemplate = templateHtml
      .replace(/{{DocTitle}}/g, t('delivery_note') || 'DELIVERY NOTE')
      .replace(/{{DateLabel}}/g, t('date') || 'Date')
      .replace(/{{FromLabel}}/g, t('from') || 'From')
      .replace(/{{ToLabel}}/g, t('to') || 'To')
      .replace(/{{ProductHeader}}/g, t('product') || 'Product')
      .replace(/{{QuantityHeader}}/g, t('quantity') || 'Quantity')
      .replace(/{{TotalLabel}}/g, t('total') || 'Total')
      .replace(/{{FooterText}}/g, t('thank_you_for_your_business') || 'Thank you for your business!')
      .replace(/{{CompanyName}}/g, storeInfo?.name || '')
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || '')
      .replace(/{{CompanyEmail}}/g, storeInfo?.email || '')
      .replace(/{{CompanyPhone}}/g, storeInfo?.phone || '')
      .replace(/{{CompanyAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCity}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankAccount}}/g, storeInfo?.iban || '')
      .replace(/{{CustomerName}}/g, customerName)
      .replace(/{{CustomerId}}/g, String(deliveryNote.CustomerId || customer.Id || ''))
      .replace(/{{CustomerVAT}}/g, customerVAT)
      .replace(/{{CustomerNumber}}/g, String(deliveryNote.CustomerId || customer.Id || ''))
      .replace(/{{CustomerEmail}}/g, customerEmail)
      .replace(/{{CustomerPhone}}/g, customerPhone)
      .replace(/{{CustomerAddress}}/g, customerStreet)
      .replace(/{{CustomerCity}}/g, customerCity)
      .replace(/{{CustomerStreetAddress}}/g, customerStreet)
      .replace(/{{CustomerCityCountry}}/g, customerCityCountry)
      .replace(/{{DeliveryNoteNumber}}/g, deliveryNote.DeliveryNoteNumber || '')
      .replace(/{{DeliveryNoteDate}}/g, formattedDateTime)
      .replace(/{{DeliveryTime}}/g, deliveryTime)
      .replace(/{{Status}}/g, '')
      .replace(/status-{{Status}}/g, '')
      .replace(/{{LineItems}}/g, ProductRows)
      .replace(/{{{ProductRows}}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, String(deliveryNote.TotalQuantity || 0))
      .replace(/{{{DiscountRow}}}/g, '')
      .replace(/{{Subtotal}}/g, `€${parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2)}`)
      .replace(/{{TotalExcl}}/g, parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2))
      .replace(/{{VATPercentage}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{VAT}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{TotalVAT}}/g, parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2))
      .replace(/{{VATAmount}}/g, `€${parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2)}`)
      .replace(/{{Discount}}/g, '€0.00')
      .replace(/{{TotalAmount}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{Total}}/g, `€${parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2)}`)
      .replace(/{{GrandTotal}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{InvoicePaidAmount}}/g, parseFloat(deliveryNote.PaidAmount || deliveryNote.InvoicePaidAmount || 0).toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, parseFloat(deliveryNote.DueAmount || deliveryNote.InvoiceDueAmount || deliveryNote.TotalValue || deliveryNote.TotalAmount || 0).toFixed(2))
      .replace(/{{{PaymentMethodsSection}}}/g, '')
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1')
      .replace(/{{VATSection}}/g, '')
      .replace(/{{CustomText}}/g, deliveryNote.Notes || deliveryNote.CustomText || '')
      .replace(/{{CustomTextSection}}/g, (deliveryNote.Notes || deliveryNote.CustomText) ? `<div style="margin-top: 16px; padding: 12px; background: #f9f9f9; border-left: 3px solid #667eea; font-size: 8.5pt;"><strong>Notes:</strong><br>${deliveryNote.Notes || deliveryNote.CustomText}</div>` : '')
      .replace(/{{Notes}}/g, deliveryNote.Notes || '');

    // Force print colors and remove box-shadow for clean PDF capture
    filledTemplate = filledTemplate.replace(
      '</style>',
      `  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .delivery-note-container { box-shadow: none !important; margin: 0 !important; width: 794px !important; max-width: 794px !important; }
  </style>`
    );

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-99999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '794px';   // A4 width at 96 DPI
    tempDiv.style.height = '1123px'; // A4 height at 96 DPI
    tempDiv.style.background = '#ffffff';
    tempDiv.style.overflow = 'hidden';
    document.body.appendChild(tempDiv);

    // Force layout: flex column so footer sits at the very bottom
    const containerEl = tempDiv.querySelector('.page') || tempDiv.querySelector('.delivery-note-container');
    if (containerEl) {
      containerEl.style.height = '1123px';
      containerEl.style.minHeight = '1123px';
      containerEl.style.display = 'flex';
      containerEl.style.flexDirection = 'column';
      containerEl.style.overflow = 'hidden';
      containerEl.style.paddingBottom = '20px';
      containerEl.style.marginBottom = '0';
      const cw = containerEl.querySelector('.content-wrapper');
      if (cw) {
        cw.style.flex = '1 1 auto';
        cw.style.minHeight = '0';
      }
      const ftr = containerEl.querySelector('.footer');
      if (ftr) {
        ftr.style.marginTop = 'auto';
        ftr.style.paddingBottom = '10px';
        ftr.style.marginBottom = '0';
      }
    }

    // Wait for images/fonts to settle
    await new Promise(resolve => setTimeout(resolve, 120));

    const canvas = await html2canvas(tempDiv, {
      scale: 1.5,  // Reduced from 2 to 1.5 for smaller file size while maintaining good quality
      useCORS: true,
      logging: false,
      width: 794,
      height: 1123,
      windowWidth: 794,
      windowHeight: 1123,
      backgroundColor: '#ffffff',
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0) {
      throw new Error('Invalid canvas generated');
    }

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    const pdfBlob = pdf.output('blob');
    window.open(URL.createObjectURL(pdfBlob), '_blank');

    return true;
  } catch (error) {
    toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF. Please try again.');
    return false;
  }
};

/**
 * Calculate due date based on invoice/delivery note date and payment term days
 */
export const calculateDueDate = (startDate, paymentTermDays = 15) => {
  const dueDate = new Date(startDate);
  dueDate.setDate(dueDate.getDate() + paymentTermDays);
  return dueDate;
};

/**
 * Format date to local string
 */
export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Format currency
 */
export const formatCurrency = (amount, currency = "EUR") => {
  if (isNaN(amount)) return "€0.00";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

/**
 * Generate delivery note PDF HTML from template
 */
export const generateDeliveryNotePdfHtml = (
  templateHtml,
  deliveryNoteData,
  companyData
) => {
  let html = templateHtml;

  // Replace delivery note placeholders
  html = html.replace(/{{DeliveryNoteNumber}}/g, deliveryNoteData.deliveryNoteNumber || "");
  html = html.replace(/{{DeliveryNoteDate}}/g, formatDate(deliveryNoteData.deliveryNoteDate));
  html = html.replace(/{{Status}}/g, deliveryNoteData.status || "Pending");

  // Replace company placeholders
  html = html.replace(/{{CompanyName}}/g, companyData.companyName || "");
  html = html.replace(/{{CompanyAddress}}/g, companyData.street || "");
  html = html.replace(/{{CompanyCity}}/g, `${companyData.postCode || ""} ${companyData.city || ""}`);
  html = html.replace(/{{CompanyCountry}}/g, companyData.country || "");
  html = html.replace(/{{CompanyVAT}}/g, companyData.vatNumber || "");
  html = html.replace(/{{CompanyPhone}}/g, companyData.phone || "");
  html = html.replace(/{{CompanyEmail}}/g, companyData.email || "");
  html = html.replace(/{{CompanyBankAccount}}/g, companyData.bankAccount || "");

  // Replace customer placeholders
  const customer = deliveryNoteData.customer || {};
  html = html.replace(/{{CustomerName}}/g, customer.Name || customer.CompanyName || "");
  html = html.replace(/{{CustomerAddress}}/g, customer.Street || "");
  html = html.replace(/{{CustomerCity}}/g, `${customer.PostCode || ""} ${customer.City || ""}`);
  html = html.replace(/{{CustomerCountry}}/g, customer.Country || "");
  html = html.replace(/{{CustomerVAT}}/g, customer.VATNumber || "");
  html = html.replace(/{{CustomerEmail}}/g, customer.Email || "");
  html = html.replace(/{{CustomerPhone}}/g, customer.PhoneNumber || "");

  // Generate line items HTML
  const lineItemsHtml = deliveryNoteData.products
    .map(
      (product, index) => `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">${index + 1}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">${product.ProductName || ""}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center;">${product.Quantity || 0}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(product.Price || 0)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency((product.Quantity || 0) * (product.Price || 0))}</td>
    </tr>
  `
    )
    .join("");

  html = html.replace(/{{LineItems}}/g, lineItemsHtml);

  // Calculate totals
  const subtotal = deliveryNoteData.products.reduce(
    (sum, product) => sum + (product.Quantity || 0) * (product.Price || 0),
    0
  );

  const discount = deliveryNoteData.discountValue || 0;
  const vatPercentage = deliveryNoteData.vatPercentage || 0;
  
  let discountAmount = 0;
  if (deliveryNoteData.discountType === "percentage") {
    discountAmount = (subtotal * discount) / 100;
  } else {
    discountAmount = discount;
  }

  const totalBeforeVat = subtotal - discountAmount;
  const vatAmount = (totalBeforeVat * vatPercentage) / 100;
  const total = totalBeforeVat + vatAmount;

  // Calculate payment amounts
  const totalAmount = total;
  const paidAmount = Number(deliveryNoteData.invoicePaidAmount || 0);
  const dueAmount = total - paidAmount;

  // Build payment methods section for multiple payments
  let paymentMethodsSection = '';
  if (deliveryNoteData.paymentMethods && deliveryNoteData.paymentMethods.length > 0 && paidAmount > 0) {
    const paymentMethodsList = deliveryNoteData.paymentMethods
      .map(pm => `<div class="total-row"><span>${pm.name || pm.PaymentMethodName || 'Payment'}:</span><strong>€${Number(pm.amount || pm.Amount || 0).toFixed(2)}</strong></div>`)
      .join('');
    paymentMethodsSection = paymentMethodsList;
  } else if (paidAmount > 0 && deliveryNoteData.paymentMethod && deliveryNoteData.paymentMethod !== '') {
    paymentMethodsSection = `<div class="total-row"><span>Payment Method:</span><strong>${deliveryNoteData.paymentMethod}</strong></div>`;
  }

  // Replace totals
  html = html.replace(/{{Subtotal}}/g, formatCurrency(subtotal));
  html = html.replace(/{{Discount}}/g, formatCurrency(discountAmount));
  html = html.replace(/{{VATPercentage}}/g, vatPercentage.toFixed(0));
  html = html.replace(/{{VAT}}/g, vatPercentage.toFixed(0));
  html = html.replace(/{{VATAmount}}/g, formatCurrency(vatAmount));
  html = html.replace(/{{TotalVAT}}/g, vatAmount.toFixed(2));
  html = html.replace(/{{Total}}/g, formatCurrency(total));
  html = html.replace(/{{GrandTotal}}/g, total.toFixed(2));
  html = html.replace(/{{TotalAmount}}/g, totalAmount.toFixed(2));

  // Replace payment information
  html = html.replace(/{{{PaymentMethodsSection}}}/g, paymentMethodsSection);
  html = html.replace(/{{InvoicePaidAmount}}/g, paidAmount.toFixed(2));
  html = html.replace(/{{InvoiceDueAmount}}/g, dueAmount >= 0 ? dueAmount.toFixed(2) : '0.00');

  // Replace page numbering
  html = html.replace(/{{PageNumber}}/g, '1');
  html = html.replace(/{{TotalPages}}/g, '1');

  // Replace notes/custom text
  html = html.replace(/{{Notes}}/g, deliveryNoteData.notes || deliveryNoteData.customText || "");
  html = html.replace(/{{CustomText}}/g, deliveryNoteData.customText || deliveryNoteData.notes || "");
  html = html.replace(/{{CustomTextSection}}/g, deliveryNoteData.customText 
    ? `<div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-left: 4px solid #667eea; border-radius: 5px;"><strong style="color: #667eea;">Additional Notes:</strong><br><div style="margin-top: 8px; line-height: 1.6;">${deliveryNoteData.customText}</div></div>` 
    : '');

  return html;
};
